/**
 * Product Form Upsells Integration
 * Intercepts form submit to include upsell products
 */

(function() {
  'use strict';

  // Prevent multiple submissions
  let isSubmitting = false;

  // Wait for DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    console.log('[Product Form Upsells] Initializing...');
    interceptProductForms();
  }

  /**
   * Intercept all product form submissions
   */
  function interceptProductForms() {
    const forms = document.querySelectorAll('form[action*="/cart/add"]');
    
    forms.forEach(form => {
      // Use capture phase to intercept early
      form.addEventListener('submit', handleFormSubmit, true);
    });
  }

  /**
   * Handle product form submission
   */
  async function handleFormSubmit(event) {
    const form = event.target;

    // Prevent multiple simultaneous submissions
    if (isSubmitting) {
      console.log('[Product Form Upsells] Already submitting, ignoring duplicate');
      event.preventDefault();
      event.stopImmediatePropagation();
      return false;
    }

    console.log('[Product Form Upsells] Form submitted');

    // CRITICAL: Check for upsells SYNCHRONOUSLY before any await.
    // In async event handlers, preventDefault() only works if called
    // before the first await — otherwise the event has already propagated
    // to other listeners (e.g. ProductForm.onSubmitHandler).
    const selectedUpsells = getSelectedUpsells();
    const unlockedGifts = getUnlockedGifts();

    console.log('[Product Form Upsells] Selected upsells:', selectedUpsells);

    // Variables for button state management (declared here for finally block access)
    let submitButtons = [];
    let originalStates = [];

    // If no upsells and no unlocked gifts, let form submit normally
    if (selectedUpsells.length === 0 && unlockedGifts.length === 0) {
      console.log('[Product Form Upsells] No upsells/gifts to add, allowing normal form submission');
      return; // Don't prevent default, let form submit normally
    }

    // Only intercept if we have upsells to add
    // MUST be called synchronously (before any await) to actually prevent
    // the theme's ProductForm.onSubmitHandler from also adding the main product
    event.preventDefault();
    event.stopImmediatePropagation(); // Stop other listeners
    
    // Set submitting flag and disable button
    isSubmitting = true;
    
    // Disable all submit buttons and show loading state
    submitButtons = Array.from(form.querySelectorAll('button[type="submit"], input[type="submit"]'));
    originalStates = [];
    
    submitButtons.forEach((button, index) => {
      originalStates[index] = {
        disabled: button.disabled,
        text: button.textContent || button.value,
        spinner: button.querySelector('.loading-overlay__spinner')
      };
      button.disabled = true;
      
      // Show loading spinner if it exists
      if (originalStates[index].spinner) {
        originalStates[index].spinner.classList.remove('hidden');
      }
      
      // Update button text if possible
      if (button.tagName === 'BUTTON' && button.querySelector('span')) {
        const buttonText = button.querySelector('span');
        if (buttonText && !buttonText.textContent.includes('Adding') && !buttonText.textContent.includes('sold_out')) {
          originalStates[index].originalText = buttonText.textContent;
          buttonText.textContent = 'Adding...';
        }
      }
    });
    
    // Get form data
    const formData = new FormData(form);
    const mainProductId = formData.get('id');
    const mainProductQuantity = formData.get('quantity') || 1;
    
    // Prepare items array
    const items = [
      {
        id: mainProductId,
        quantity: parseInt(mainProductQuantity)
      }
    ];
    
    // Get cart data once to check quantities for all upsells
    let cartData = null;
    try {
      const cartResponse = await fetch('/cart.js');
      cartData = await cartResponse.json();
    } catch (error) {
      console.warn('[Product Form Upsells] Could not fetch cart data for quantity check:', error);
    }
    
    // Helper to get current quantity from cart data
    function getCurrentQuantityFromCart(variantId, cart) {
      if (!cart || !cart.items) return 0;
      const variantIdNum = parseInt(variantId, 10);
      const item = cart.items.find(i => {
        if (!i || !i.variant_id) return false;
        const itemVariantId = parseInt(i.variant_id, 10);
        return itemVariantId === variantIdNum;
      });
      return item ? item.quantity : 0;
    }
    
    // Add upsell products
    for (const upsell of selectedUpsells) {
      // Check if product is free (should match main product quantity)
      const isFree = upsell.isFree || false;
      let quantity = isFree ? parseInt(mainProductQuantity) : 1;
      
      // Check max quantity limit
      const maxQuantity = upsell.maxQuantity || null;
      if (maxQuantity !== null && maxQuantity > 0 && cartData) {
        // Get current quantity in cart for this variant
        const currentCartQuantity = getCurrentQuantityFromCart(upsell.variantId, cartData);
        const availableQuantity = maxQuantity - currentCartQuantity;
        
        if (availableQuantity <= 0) {
          console.log('[Product Form Upsells] Skipping upsell - max quantity reached:', upsell.variantId, 'max:', maxQuantity, 'current:', currentCartQuantity);
          continue; // Skip this upsell
        }
        
        // Limit quantity to available amount
        quantity = Math.min(quantity, availableQuantity);
        console.log('[Product Form Upsells] Limiting quantity for upsell:', upsell.variantId, 'to', quantity, '(max:', maxQuantity, 'current:', currentCartQuantity, ')');
      }
      
      items.push({
        id: upsell.variantId,
        quantity: quantity,
        properties: {
          _upsell: 'true'
        }
      });
    }
    // Add unlocked gifts automatically
    unlockedGifts.forEach(gift => {
      items.push({
        id: gift.variantId,
        quantity: 1,
        properties: {
          _gift: 'true'
        }
      });
    });
    
    console.log('[Product Form Upsells] Adding items:', items);
    
    // Add all items at once
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items })
      });
      
      const data = await response.json();
      console.log('[Product Form Upsells] Added to cart:', data);
      
      // Trigger success event immediately (no wait)
      document.dispatchEvent(new CustomEvent('cart:items-added', { 
        detail: { items: data.items }
      }));
      
      // Update cart count immediately using response
      updateCartCount(data.item_count);
      
      // Refresh cart drawer with new content and open it (optimized)
      await refreshAndOpenCartDrawer();
      
    } catch (error) {
      console.error('[Product Form Upsells] Error adding to cart:', error);
      
      // Show error message
      alert('Error adding products to cart. Please try again.');
    } finally {
      // Re-enable buttons and restore original states
      submitButtons.forEach((button, index) => {
        const originalState = originalStates[index];
        if (originalState) {
          button.disabled = originalState.disabled;
          
          // Hide loading spinner if it exists
          if (originalState.spinner) {
            originalState.spinner.classList.add('hidden');
          }
          
          // Restore button text
          if (button.tagName === 'BUTTON' && button.querySelector('span')) {
            const buttonText = button.querySelector('span');
            if (buttonText && originalState.originalText) {
              buttonText.textContent = originalState.originalText;
            }
          }
        }
      });
      
      // Reset submitting flag
      setTimeout(() => {
        isSubmitting = false;
      }, 1000); // Wait 1 second before allowing next submission
    }
  }

  /**
   * Get all selected upsells on the page
   */
  function getSelectedUpsells() {
    const upsells = [];
    const seenVariantIds = new Set(); // Track to avoid duplicates
    
    // Find all upsell containers
    const upsellCards = document.querySelectorAll('[data-upsell-variant-id]');
    
    console.log('[Product Form Upsells] Found', upsellCards.length, 'upsell cards');
    
    upsellCards.forEach(card => {
      const variantId = card.dataset.upsellVariantId;
      const style = card.dataset.upsellStyle;
      
      // Skip if we've already added this variant
      if (seenVariantIds.has(variantId)) {
        console.log('[Product Form Upsells] Skipping duplicate variant:', variantId);
        return;
      }
      
      // Check if preselected (preselected = toggle ON by default in HTML)
      const isPreselected = card.hasAttribute('data-upsell-preselected') && 
                           card.getAttribute('data-upsell-preselected') === 'true';
      
      // Check if product is free (100% discount)
      const isFree = card.hasAttribute('data-is-free') && 
                    card.getAttribute('data-is-free') === 'true';
      
      let isSelected = false;
      
      // SPECIAL CASE: 100% off items (toggle is hidden)
      // If preselected and 100% off, auto-add without checking toggle
      if (isFree && isPreselected) {
        // For 100% off preselected items, toggle is hidden, so auto-add
        isSelected = true;
        console.log('[Product Form Upsells] 100% off preselected item - auto-adding:', card.dataset.upsellHandle);
      } else {
        // Normal items: check toggle state
        // CRITICAL: Always check toggle state directly from DOM (source of truth)
        // Preselected items start with toggle ON, but user can toggle them OFF
        // Final decision: only add if toggle is currently ON (checked = true)
        if (style === 'toggle_switch') {
          const toggle = card.querySelector('.product-upsell-toggle');
          // Double-check: read directly from DOM, not cached state
          if (toggle) {
            // Force a re-read of the checked property to ensure we get the latest state
            // This is critical when user quickly toggles off then clicks add to cart
            isSelected = toggle.checked === true;
            
            // Don't check offsetParent for hidden toggles - we already handled 100% off above
            // Just check if disabled
            if (toggle.disabled) {
              isSelected = false;
            }
            
            // Log for debugging preselected items
            if (isPreselected) {
              console.log('[Product Form Upsells] Preselected upsell - toggle state:', toggle.checked, '→ will add:', isSelected);
            }
          } else {
            // Toggle not found - this might be a 100% off item that we should handle above
            // But if it's not preselected, don't add
            if (!isPreselected) {
              console.warn('[Product Form Upsells] Toggle not found and not preselected for style:', style);
              isSelected = false;
            }
          }
        } else if (style === 'checkbox') {
          const checkbox = card.querySelector('.product-upsell-checkbox');
          if (checkbox) {
            isSelected = checkbox.checked === true;
            if (checkbox.disabled) {
              isSelected = false;
            }
          }
        } else if (style === 'button') {
          // Buttons don't have persistent selection state
          // They add immediately, so skip them here
          isSelected = false;
        }
      }
      
      // IMPORTANT LOGIC:
      // - 100% off + preselected = auto-add (toggle is hidden, no way to uncheck)
      // - Normal preselected = toggle ON by default (set in HTML via Liquid)
      // - User can toggle normal items OFF if they don't want it
      // - Final decision: only add if toggle is currently ON (checked = true) OR if 100% off + preselected
      // - If user toggled OFF, toggle.checked will be false → don't add
      
      if (isSelected && variantId) {
        seenVariantIds.add(variantId); // Mark as seen
        
        // isFree was already checked above, reuse it
        
        // Get max quantity setting
        const maxQuantityAttr = card.getAttribute('data-max-quantity');
        const maxQuantity = maxQuantityAttr ? parseInt(maxQuantityAttr, 10) : null;
        
        upsells.push({
          variantId: variantId,
          handle: card.dataset.upsellHandle,
          isFree: isFree,
          maxQuantity: maxQuantity
        });
        console.log('[Product Form Upsells] Added upsell:', card.dataset.upsellHandle, variantId, isPreselected ? '(preselected)' : '', isFree ? '(free, will match main quantity)' : '', maxQuantity ? `(max qty: ${maxQuantity})` : '');
      }
    });
    
    console.log('[Product Form Upsells] Total unique upsells:', upsells.length);
    return upsells;
  }

  /**
   * Get unlocked gifts from quantity-gifts component
   * Automatically included on submit (no toggle required)
   */
  function getUnlockedGifts() {
    const gifts = [];
    const container = document.querySelector('quantity-gifts');
    if (!container) return gifts;

    const unlocked = container.querySelectorAll('.quantity-gift[data-unlocked="true"]');
    unlocked.forEach(card => {
      const variantId = card.dataset.product;
      if (variantId) {
        gifts.push({ variantId });
      }
    });
    if (gifts.length > 0) {
      console.log('[Product Form Upsells] Unlocked gifts to add:', gifts);
    }
    return gifts;
  }

  /**
   * Refresh cart drawer content and open it
   */
  async function refreshAndOpenCartDrawer() {
    try {
      console.log('[Product Form Upsells] Refreshing cart drawer...');
      
      // Method 1: Use Shopify sections API
      // Use section_id format (more reliable than sections parameter)
      const sectionUrl = '/?section_id=cart-drawer';
      const response = await fetch(sectionUrl);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch cart drawer: ${response.status}`);
      }
      
      const html = await response.text();
      
      // Parse the response
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Find the cart drawer in the response
      const newCartDrawer = doc.querySelector('cart-drawer');
      
      if (newCartDrawer) {
        // Find the cart drawer and update its content
        const cartDrawer = document.querySelector('cart-drawer');
        
        if (cartDrawer) {
          // Update the entire cart drawer content
          const currentCartDrawer = cartDrawer.querySelector('#CartDrawer');
          const newCartDrawerContent = newCartDrawer.querySelector('#CartDrawer');
          
          if (currentCartDrawer && newCartDrawerContent) {
            console.log('[Product Form Upsells] Updating cart drawer HTML (selective update to preserve upsells)');
            
            // ✅ FIX: Update cart drawer content completely
            // First, update the entire CartDrawer inner content
            console.log('[Product Form Upsells] Updating entire cart drawer content');
            currentCartDrawer.innerHTML = newCartDrawerContent.innerHTML;
            
            // Remove is-empty class from both cart-drawer and cart-drawer-items
            cartDrawer.classList.remove('is-empty');
            const cartDrawerItems = document.querySelector('cart-drawer-items');
            if (cartDrawerItems) {
              cartDrawerItems.classList.remove('is-empty');
            }
            
            // Trigger cart update event immediately (no wait)
            document.dispatchEvent(new CustomEvent('cart:updated'));
            
            // Re-initialize cart drawer items if it's a web component
            const cartItemsElement = document.querySelector('cart-drawer-items, cart-items');
            if (cartItemsElement) {
              console.log('[Product Form Upsells] Found cart items element, triggering refresh');
              // Dispatch event to refresh component
              cartItemsElement.dispatchEvent(new CustomEvent('cart:updated'));
              // Also try calling refresh method if it exists
              if (typeof cartItemsElement.refresh === 'function') {
                cartItemsElement.refresh();
              }
            }
            
            // Verify content was updated
            const cartItems = cartDrawer.querySelector('.cart-items, .drawer__cart-items-wrapper, cart-drawer-items, table.cart-items');
            if (cartItems) {
              console.log('[Product Form Upsells] ✅ Cart items found in drawer');
            } else {
              console.warn('[Product Form Upsells] ⚠️ Cart items not found - drawer might be empty');
            }
            
            // Wait briefly for DOM to settle before opening drawer
            await new Promise(resolve => setTimeout(resolve, 150));
            
            // Re-attach cart upsell listeners after refresh (defer to after drawer opens)
            setTimeout(() => {
              if (window.CartUpsell && typeof window.CartUpsell.init === 'function') {
                console.log('[Product Form Upsells] Re-initializing cart upsells');
                window.CartUpsell.init();
              }
              
              // Re-initialize carousel if it exists
              if (window.initCartDrawerCarousels && typeof window.initCartDrawerCarousels === 'function') {
                console.log('[Product Form Upsells] Re-initializing cart drawer carousel');
                window.initCartDrawerCarousels();
              }
            }, 50);
            
            // Open the drawer immediately
            console.log('[Product Form Upsells] Opening cart drawer...');
            
            // Method 1: Use open() method if available
            if (typeof cartDrawer.open === 'function') {
              console.log('[Product Form Upsells] Using open() method');
              cartDrawer.open();
            } else {
              // Method 2: Manual class addition
              console.log('[Product Form Upsells] Using manual class method');
              cartDrawer.classList.add('animate', 'active');
              document.body.classList.add('overflow-hidden');
              
              // Ensure drawer is visible
              const drawer = document.querySelector('.drawer');
              if (drawer) {
                drawer.style.visibility = 'visible';
              }
            }
            
            return; // Success!
          }
        }
      }
      
      // If sections API didn't work, try fallback
      console.warn('[Product Form Upsells] Sections API failed, trying fallback...');
      
      // Fallback: Just open and hope it refreshes
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        // Try clicking cart icon which might trigger refresh
        const cartIcon = document.querySelector('#cart-icon-bubble, .header__icon--cart, [href="/cart"]');
        if (cartIcon) {
          console.log('[Product Form Upsells] Clicking cart icon as fallback');
          cartIcon.dispatchEvent(new MouseEvent('click', { bubbles: true }));
        } else if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
      }
      
    } catch (error) {
      console.error('[Product Form Upsells] Error refreshing cart drawer:', error);
      
      // Last resort: page reload or just open
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    }
  }

  /**
   * Update cart count bubble
   * @param {number} providedCount - Optional count from API response (avoids extra fetch)
   */
  function updateCartCount(providedCount) {
    if (providedCount !== undefined && providedCount !== null) {
      // Use provided count (from add/remove response)
      const countBubbles = document.querySelectorAll('.cart-count-bubble, [data-cart-count]');
      countBubbles.forEach(bubble => {
        bubble.textContent = providedCount;
        if (providedCount > 0) {
          bubble.classList.remove('hidden');
        }
      });
      console.log('[Product Form Upsells] Updated cart count using provided value:', providedCount);
    } else {
      // Fallback to fetch if count not provided (should rarely happen)
      fetch('/cart.js')
        .then(res => res.json())
        .then(cart => {
          const countBubbles = document.querySelectorAll('.cart-count-bubble, [data-cart-count]');
          countBubbles.forEach(bubble => {
            bubble.textContent = cart.item_count;
            if (cart.item_count > 0) {
              bubble.classList.remove('hidden');
            }
          });
          console.log('[Product Form Upsells] Updated cart count from fetch:', cart.item_count);
        })
        .catch(error => {
          console.error('[Product Form Upsells] Error updating cart count:', error);
        });
    }
  }

})();

